import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  useCreateAddressDetailsMutation,
  useCreateAddressMutation,
  useCreateAddressDetailsUpdateMutation,
  useCreateAddressDetailsDraftMutation,
  useEditAddressDetailsDraftMutation,
} from "../../../../store/Company/companyApiSlice";
import {
  getCountryCode,
  getStatesByCountryCode,
} from "../../../../utils/countriesState";
import useCompanyUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import { addressPayload, addressSavePath, validateAddress } from "./addressFields";

/* The address form's behaviour. */

/* Transcribed default, string included. */
export const DEFAULT_HOURS = "Monday to Friday 9AM to 6PM";

export const EMPTY_ADDRESS = {
  address_line_1: "",
  address_line_2: "",
  area: "",
  postal_code: "",
  country: "",
  state: "",
  city: "",
  email: "",
  telephone: "",
  working_hours: DEFAULT_HOURS,
};

export const useAddressForm = ({ address, primaryAddress, onDone, refetch }) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  const [values, setValues] = useState(EMPTY_ADDRESS);
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [countries] = useState(() => getCountryCode());
  /* Transcribed: only the create path sends this, and the live checkbox that
     set it is not rendered — so it is always false there too. */
  const [primaryAdd] = useState(Boolean(primaryAddress) && false);

  const [createAddressDetails, { isLoading: creating }] = useCreateAddressDetailsMutation();
  const [setAddressConnection] = useCreateAddressMutation();
  const [addressDetailsUpdate, { isLoading: updating }] = useCreateAddressDetailsUpdateMutation();
  const [createAddressDetailsDraft, { isLoading: drafting }] = useCreateAddressDetailsDraftMutation();
  const [editAddressDetailsDraft] = useEditAddressDetailsDraftMutation();

  const handleUpdateCompanyFollow = useCompanyUpdate();

  /* Transcribed from the live effect: the record's values are loaded, and the
     state list for its country is fetched so the State control can be a select
     rather than a free-text box. */
  useEffect(() => {
    if (!address) {
      setValues(EMPTY_ADDRESS);
      setStates([]);
      return;
    }

    setValues({
      address_line_1: address.address_line_1 || "",
      address_line_2: address.address_line_2 || "",
      area: address.area || "",
      postal_code: address.postal_code || "",
      country: address.country || "",
      state: address.state || "",
      city: address.city || "",
      email: address.email || "",
      telephone: address.telephone || "",
      /* Transcribed: the stored string wins, else the default. */
      working_hours: address.working_hours || DEFAULT_HOURS,
    });

    let cancelled = false;
    (async () => {
      const match = countries.find((c) => c.countryCode === address.country);
      if (!match) return;
      const list = await getStatesByCountryCode(match.countryCode);
      if (!cancelled) setStates(list || []);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const setField = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  /* Transcribed: changing the country reloads its states. */
  const onCountryChange = async (countryCode) => {
    setField("country", countryCode);
    const match = countries.find((c) => c.countryCode === countryCode);
    if (!match) return;
    const list = await getStatesByCountryCode(match.countryCode);
    setStates(list || []);
  };

  /* Transcribed: the country control is unusable once the record being edited already has one. */
  const countryLocked = address != null && Boolean(address?.country);

  const submit = async () => {
    const found = validateAddress({ ...values, companyID: currentEditingCompany?.companyID });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const path = addressSavePath({
      verifiedCompany: currentEditingCompany?.verifiedCompany,
      address,
    });
    const withCompany = { ...values, companyID: currentEditingCompany?.companyID };

    try {
      if (path === "editDraft") {
        await editAddressDetailsDraft({
          data: addressPayload(withCompany, {
            draft: true,
            linkedId: address?.company_address_detailsID_id,
          }),
          token,
          company_address_details_draftID: address?.company_address_details_draftID,
        }).unwrap();
      } else if (path === "createDraft") {
        await createAddressDetailsDraft({
          data: addressPayload(withCompany, {
            draft: true,
            linkedId: address?.company_address_detailsID,
          }),
          token,
          company_address_detailsID: address?.company_address_detailsID,
        }).unwrap();
      } else if (path === "update") {
        const res = await addressDetailsUpdate({
          data: addressPayload(withCompany),
          token,
          company_address_detailsID: address?.company_address_detailsID,
        }).unwrap();

        /* Transcribed: followers are told the details changed. */
        if (res) {
          const result = await handleUpdateCompanyFollow(
            currentEditingCompany.companyID,
            "Address Details Updated",
            token
          );
          if (!result.success) console.error(result.error);
        }
      } else {
        const res = await createAddressDetails({
          data: addressPayload(withCompany),
          token,
        }).unwrap();

        /* Transcribed: a new address is created first, then LINKED to the
           company — the link is a second call and carries the primary flag. */
        if (res) {
          await setAddressConnection({
            data: {
              primary_address: primaryAdd,
              company_address_detailsID: res.company_address_detailsID,
              companyID: currentEditingCompany.companyID,
            },
            token,
            companyId: currentEditingCompany.companyID,
          }).unwrap();

          const result = await handleUpdateCompanyFollow(
            currentEditingCompany.companyID,
            "New Address Added",
            token
          );
          if (!result.success) console.error(result.error);
        }
      }
    } catch (error) {
      /* The live code swallows all three of these. Written down instead — see
         F-040, which covers the same gap across this flow. The modal is still
         closed below, as the live `finally` does. */
      console.error("Address save failed:", error);
    } finally {
      /* Transcribed: the draft paths refetch and close. */
      refetch?.();
      onDone?.();
    }
  };

  return {
    values,
    setField,
    errors,
    states,
    countries,
    countryLocked,
    onCountryChange,
    isSaving: creating || updating || drafting,
    submit,
    /* Which write a submit will perform — exposed for the tests, and it names
       the concept rather than leaving it implicit. */
    savePath: addressSavePath({
      verifiedCompany: currentEditingCompany?.verifiedCompany,
      address,
    }),
  };
};

export default useAddressForm;
